import { Region } from '../../../common/models/region';

/**
 * The region an address most likely sits in, read off its postcode.
 *
 * Austrian postcodes are handed out by province, so the code alone names the
 * province for all but a handful of border municipalities. Measured against
 * the 3.052 Austrian addresses that carry a region today, the ranges below
 * agree with 2.997 of them; a good part of the remaining 55 are wrong in the
 * data rather than wrong here (Vienna filed under Lower Austria, Lech under
 * Tyrol). Entering it by hand was a required field nobody could guess from
 * the form (#2212, R-31).
 *
 * This is a suggestion, never a decision: it fills the field only while it is
 * empty, and the list it fills stays open for the few places the ranges get
 * wrong.
 *
 * Only Austria. German and Swiss postcodes carry no comparable rule — their
 * leading digits cross state and canton borders — so those addresses keep
 * asking.
 */
interface ZipRange {
    from: number;
    to: number;
    /** ISO 3166-2, the same code the region list is keyed by. */
    code: string;
}

const AT_RANGES: ZipRange[] = [
    { from: 1000, to: 1999, code: 'AT-9' }, // Wien
    { from: 2000, to: 3999, code: 'AT-3' }, // Niederösterreich
    { from: 4000, to: 4999, code: 'AT-4' }, // Oberösterreich
    { from: 5000, to: 5999, code: 'AT-5' }, // Salzburg
    { from: 6000, to: 6699, code: 'AT-7' }, // Tirol
    { from: 6700, to: 6999, code: 'AT-8' }, // Vorarlberg
    { from: 7000, to: 7999, code: 'AT-1' }, // Burgenland
    { from: 8000, to: 8999, code: 'AT-6' }, // Steiermark
    { from: 9000, to: 9899, code: 'AT-2' }, // Kärnten
    // East Tyrol is an exclave behind Carinthia and keeps the 99xx block.
    { from: 9900, to: 9999, code: 'AT-7' },
];

const RANGES: Record<string, ZipRange[]> = { AT: AT_RANGES };

/**
 * @param zip      as typed, so a stray space or a country prefix is tolerated
 * @param country  ISO 3166-1 alpha-2
 * @param regions  what the country offers; nothing is suggested that is not in it
 */
export const regionFromZip = <T extends Region>(
    zip: string | undefined,
    country: string | undefined,
    regions: T[]
): T | undefined => {
    const ranges = country && RANGES[country.toUpperCase()];

    if (!ranges || !zip) {
        return undefined;
    }

    const digits = zip.replace(/\D/g, '');

    if (4 !== digits.length) {
        return undefined;
    }

    const number = Number(digits);
    const range = ranges.find(entry => number >= entry.from && number <= entry.to);

    return range ? regions.find(region => region.code === range.code) : undefined;
};
